import React from 'react'

import { cn } from '@/lib/ui/style-helpers'

export interface HighlightedTextProps {
  text: string
  highlights?: { start: number; end: number }[]
  className?: string
  highlightClassName?: string
}

/**
 * Component that highlights specific ranges of text
 * Used for showing search matches with highlighting
 */
export const HighlightedText = React.forwardRef<HTMLSpanElement, HighlightedTextProps>(
  ({ text, highlights = [], className, highlightClassName, ...props }, ref) => {
    // If no highlights, return plain text
    if (highlights.length === 0) {
      return (
        <span ref={ref} className={className} {...props}>
          {text}
        </span>
      )
    }

    // Sort highlights by start position to handle overlapping/out-of-order highlights
    const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start)

    // Merge overlapping highlights
    const mergedHighlights: { start: number; end: number }[] = []
    for (const highlight of sortedHighlights) {
      const lastMerged = mergedHighlights[mergedHighlights.length - 1]
      if (lastMerged && highlight.start <= lastMerged.end) {
        // Overlapping - extend the previous highlight
        lastMerged.end = Math.max(lastMerged.end, highlight.end)
      } else {
        // No overlap - add new highlight
        mergedHighlights.push({ ...highlight })
      }
    }

    // Build array of text segments with highlight information
    const segments: { text: string; isHighlighted: boolean }[] = []
    let currentIndex = 0

    for (const highlight of mergedHighlights) {
      const start = Math.max(0, highlight.start)
      const end = Math.min(text.length, highlight.end)

      // Add non-highlighted text before this highlight
      if (currentIndex < start) {
        segments.push({
          text: text.slice(currentIndex, start),
          isHighlighted: false,
        })
      }

      // Add highlighted text
      if (start < end) {
        segments.push({
          text: text.slice(start, end),
          isHighlighted: true,
        })
      }

      currentIndex = end
    }

    // Add remaining non-highlighted text
    if (currentIndex < text.length) {
      segments.push({
        text: text.slice(currentIndex),
        isHighlighted: false,
      })
    }

    return (
      <span ref={ref} className={className} {...props}>
        {segments.map((segment, index) =>
          segment.isHighlighted ? (
            <mark
              key={index}
              className={cn('rounded-sm bg-yellow-200 px-0.5 text-yellow-900', highlightClassName)}
            >
              {segment.text}
            </mark>
          ) : (
            <span key={index}>{segment.text}</span>
          ),
        )}
      </span>
    )
  },
)

HighlightedText.displayName = 'HighlightedText'
